/**
 * Keeps the Cell Mapping (SpreadSheetBuilder_DataParsing) in step with what the
 * designer typed on the sheet, so the two cannot disagree after a save:
 *   • `=FIELD("NAME")` (or `[NAME]`) typed into an UNMAPPED cell of a catalog field → the
 *     field is mapped there (Studio parity: dropping / typing a field maps it);
 *   • a MAPPED cell given another formula → the mapping's formula follows it (and its
 *     field, when the new formula names another catalog field);
 *   • a MAPPED cell cleared → the mapping is removed;
 *   • a MAPPED cell given literal text → the mapping stays as an entry cell (no formula).
 */
import type { FieldOption, TemplateDetail, TemplateParsing } from './sdmsTemplateApi';
import type { SheetEdits } from './sheetEdits';
import { inRange, sheetOf } from './templateWorkbook';

const FIELD_RE = /^=?\s*FIELD\(\s*"([^"]+)"\s*\)\s*$/i;

export interface MappingSyncResult { template: TemplateDetail; mapped: number; updated: number; removed: number }

export function syncMappingFromEdits(
  t: TemplateDetail,
  edits: SheetEdits,
  catalog: FieldOption[],
  detailBandOf: (sheetIndex: number) => string | undefined,
  newId: () => number,
): MappingSyncResult {
  const byName = new Map(catalog.filter(f => f.fieldName && f.resultType === 'RawDataTable')
    .map(f => [f.fieldName!.toUpperCase(), f]));
  let parsing = [...t.parsing];
  let fields = [...t.fields];
  let mapped = 0, updated = 0, removed = 0;

  for (const e of edits.cells) {
    if (!e.contentChanged) continue;
    const pos = e.ref.toUpperCase();
    const i = parsing.findIndex(p => sheetOf(p) === e.sheetIndex && (p.position ?? '').toUpperCase() === pos);
    const fieldName = e.formula ? FIELD_RE.exec(e.formula)?.[1]?.toUpperCase() : undefined;
    const field = fieldName ? byName.get(fieldName) : undefined;

    if (i >= 0) {
      const p = parsing[i]!;
      if (!e.formula && !e.value) { parsing.splice(i, 1); removed++; continue; }
      const next: TemplateParsing = {
        ...p,
        formula: e.formula ?? null,
        ...(field && field.id !== p.fieldID ? { fieldID: field.id, fieldName: field.fieldName, resultType: field.resultType } : {}),
      };
      if (next.formula !== p.formula || next.fieldID !== p.fieldID) { parsing[i] = next; updated++; }
      continue;
    }
    if (!field) continue;
    parsing = [...parsing, {
      uqID: newId(), sheetID: e.sheetIndex, fieldID: field.id, fieldName: field.fieldName, resultType: field.resultType,
      position: pos, formula: `=FIELD("${fieldName}")`, format: null, continuous: inRange(pos, detailBandOf(e.sheetIndex)),
      read: true, write: true, caption: field.synonymEN || field.fieldName, defaultValue: null, usersFromDepartmentChain: false,
    }];
    if (!fields.some(f => f.fieldID === field.id)) {
      fields = [...fields, {
        uqID: newId(), fieldID: field.id, fieldName: field.fieldName, resultType: field.resultType,
        caption_EN: field.synonymEN || field.fieldName, caption_CN: null, width: 100, visible: true,
        sort: fields.reduce((m, x) => Math.max(m, x.sort), 0) + 1,
        exportSample: null, exportBlank: null, exportSpike: null, exportStandard: null, matchByHeader: false, importField: null,
      }];
    }
    mapped++;
  }
  return { template: mapped + updated + removed ? { ...t, parsing, fields } : t, mapped, updated, removed };
}
